import { useState } from 'react';
import ButtonText from '../../../components/button/button-text/ButtonText';
import List from '../../../components/text/List';
import RoundedButtonSecondary from '../../../components/button/rounded-button-secondary/RoundedButtonSecondary';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import { useProject } from '../../project-hooks/useProject';
import { useLocation } from 'react-router-dom';
import { ScrollableModalStickyBottom } from '../../../components/scrollable-modal-sticky-bottom/ScrollableModalStickyBottom';
import styles from './ReceiverInfo.module.less';
import { ContextInfoMessage } from '@sb1/ffe-context-message-react';
import { Heading2, LinkText, Paragraph, SmallText, StrongText } from '@sb1/ffe-core-react';

const ReceiverInfo = ({ receiver, largeBorderRadius = false }) => {
    const project = useProject();
    const [showReceiverInfo, setShowReceiverInfo] = useState(false);
    const location = useLocation();

    const openModal = () => {
        setShowReceiverInfo(true);
        trackEvent(`fund-as-gift-receiver-info`, 'open-modal', {
            'project-id': project?.id,
            'fund-as-gift-code': project?.fundAsGiftCode,
            url: location?.pathname,
        });
    };

    return (
        <>
            {receiver?.name ? (
                <div
                    className={[
                        styles.receiverContainer,
                        largeBorderRadius ? styles.largeBorderRadius : styles.smallBorderRadius,
                    ].join(' ')}
                >
                    <Paragraph>
                        <StrongText>{receiver.name}</StrongText> <span>mottar gaven</span>
                    </Paragraph>
                    <SmallText as={'p'}>
                        Mottakeren er eier av kontoen og bestemmer om og når gaven overføres til barnet
                    </SmallText>
                    <ButtonText className={styles.receiverReadMore} onClick={openModal}>
                        Hva betyr dette?
                    </ButtonText>
                </div>
            ) : (
                <ContextInfoMessage compact>
                    <Paragraph className={styles.noReceiverText}>
                        Når du gir et gavekort på fond til barn vil sparingen skje i foreldrenes navn, på vegne av
                        barnet
                    </Paragraph>
                    <ButtonText onClick={openModal}>Les mer</ButtonText>
                </ContextInfoMessage>
            )}
            <ScrollableModalStickyBottom
                isOpen={showReceiverInfo}
                onRequestClose={() => {
                    setShowReceiverInfo(false);
                }}
                lightTheme
                showCloseButton={true}
                title={'Sparing i foresattes navn'}
            >
                <div className={styles.infoModalContainer}>
                    <div className={styles.infoModalContent}>
                        <Paragraph className={styles.bodyText}>
                            Foreldrene kan velge å spare i sitt navn når de sparer til barna. Her er noen fordeler og
                            ulemper med det.
                        </Paragraph>
                        <Heading2 lookLike={5} className={styles.strongText}>
                            Fordeler
                        </Heading2>
                        <List className={styles.list}>
                            <li>
                                Det er foreldrene som eier kontoen og disponerer pengene. De bestemmer når beløpet skal
                                overføres til barnet. Barnet får ikke automatisk tilgang til pengene ved fylte 18 år.
                            </li>
                            <li>
                                Å spare i foreldrenes navn begrenser ikke muligheten til stipend fra Lånekassen for
                                barnet.
                            </li>
                            <li>
                                Dere unngår at Statsforvalteren tar over forvaltningen av pengene, om sparebeløpet
                                overstiger 2G (1 G = 124.028 kr per 1.5.2024).
                            </li>
                            <li>
                                Det er enkelt for foreldrene å gjøre endringer, som å bytte fond hvis de ønsker det.
                            </li>
                        </List>
                        <br />
                        <Heading2 lookLike={5}>Ulemper</Heading2>
                        <List className={styles.list}>
                            <li>
                                Pengene inngår i boet til foreldrene ved eventuell skilsmisse, konkurs eller død, og
                                kreditorer kan ta utlegg i midlene hvis foreldrene havner i økonomiske problemer.
                            </li>
                            <li>Ønsker du at gavepenger skal holdes utenfor, må dette spesifiseres i et testament.</li>
                            <li>Givere må stole på at mottakeren gir pengene videre til barnet som lovet.</li>
                        </List>
                        <br />
                        <Paragraph>
                            Vil du lære mer om forskjellene mellom å spare i barnets og foreldrenes navn kan du lese mer
                            på SpareBank 1 sine sider{' '}
                            <LinkText
                                href="https://www.sparebank1.no/nb/bank/privat/sparing/spare-i-fond/spare-i-fond-til-barn.html#ditt-eller-barnets-navn"
                                target="_blank"
                                rel="noreferrer"
                            >
                                her
                            </LinkText>
                            .
                        </Paragraph>
                        <div className={styles.button}>
                            <RoundedButtonSecondary onClick={() => setShowReceiverInfo(false)}>
                                Lukk
                            </RoundedButtonSecondary>
                        </div>
                    </div>
                </div>
            </ScrollableModalStickyBottom>
        </>
    );
};

export default ReceiverInfo;
